import { Button, Card, Flex, Popconfirm, Tag, theme, Typography } from 'antd';
import { LuArrowRight, LuCheck, LuCopy, LuPencil, LuTrash2 } from 'react-icons/lu';

import BaseText from '@components/typography/BaseText';
import { colorForKey } from '@utils/objectColor';

import ColorDot from '../ColorDot';

/**
 * A single collection card.
 *
 * Edit and delete controls render only when the caller passes their handlers,
 * and the footer action only when it passes `onSelect`. The action reads
 * "Select", or "Continue" on the card already chosen — the wizard advances on
 * either, so the chosen card offers the way forward after stepping back.
 *
 * TODO: no caller passes `onEdit`/`onDelete`/`deleting` yet — they are kept for
 * the Collections page's management work, which should wire them.
 *
 * @param {object} props
 * @param {{id, name, type, description, objects}} props.collection
 * @param {(collection) => void} [props.onSelect] - omit to render a read-only card (no footer action)
 * @param {(collection) => void} [props.onEdit]
 * @param {(collection) => void} [props.onDelete]
 * @param {boolean} [props.deleting]              - shows a spinner on the delete button
 * @param {boolean} [props.selected]              - highlights the chosen card; its action reads "Continue"
 */
const CollectionCard = ({
	collection,
	onSelect,
	onEdit,
	onDelete,
	deleting = false,
	selected = false,
}) => {
	const { token } = theme.useToken();

	const canEdit = Boolean(onEdit);
	const canDelete = Boolean(onDelete);
	const objectCount = collection.objects?.length ?? 0;
	const objectLabel = objectCount === 1 ? 'object' : 'objects';

	return (
		<Card
			size="small"
			styles={{ body: { height: '100%' } }}
			style={{
				height: '100%',
				borderColor: selected ? token.colorPrimary : undefined,
				borderWidth: selected ? token.lineWidthBold : undefined,
			}}>
			<Flex vertical gap={token.sizeSM} style={{ height: '100%' }}>
				<Flex justify="space-between" align="flex-start" gap="small">
					<Flex vertical flex={1} style={{ minWidth: 0 }}>
						<Typography.Text
							strong
							ellipsis={{ tooltip: collection.name }}
							style={{ maxWidth: '100%' }}>
							{collection.name}
						</Typography.Text>
						{collection.description ? (
							<BaseText size="sm" type="secondary">
								{collection.description}
							</BaseText>
						) : null}
					</Flex>
					<Flex align="center" gap={token.sizeXXS} style={{ flex: 'none' }}>
						<BaseText size="sm" type="secondary" style={{ whiteSpace: 'nowrap' }}>
							{objectCount} {objectLabel}
						</BaseText>
						{collection.id && (
							<Typography.Text
								copyable={{
									text: collection.id,
									icon: [<LuCopy key="copy" />, <LuCheck key="copied" />],
									tooltips: ['Copy collection ID', 'Copied'],
								}}
							/>
						)}
						{canEdit && (
							<Button
								type="text"
								size="small"
								icon={<LuPencil />}
								onClick={() => onEdit(collection)}
								aria-label={`Edit ${collection.name}`}
							/>
						)}
						{canDelete && (
							<Popconfirm
								title="Delete this collection?"
								description="This can't be undone."
								okText="Delete"
								okButtonProps={{ danger: true }}
								cancelText="Cancel"
								onConfirm={() => onDelete(collection)}>
								<Button
									type="text"
									size="small"
									danger
									loading={deleting}
									icon={<LuTrash2 />}
									aria-label={`Delete ${collection.name}`}
								/>
							</Popconfirm>
						)}
					</Flex>
				</Flex>

				<Flex gap="small" wrap>
					{collection.objects?.map(object => (
						<Tag
							key={object.key}
							bordered
							icon={<ColorDot color={object.color ?? colorForKey(object.key)} />}
							// antd spaces only its own `.anticon` icons from the label, and
							// replaces the icon's own `style` with this one.
							styles={{ icon: { marginInlineEnd: token.marginXS } }}>
							{object.label}
						</Tag>
					))}
				</Flex>

				{onSelect && (
					<div style={{ marginTop: 'auto' }}>
						<Button
							type={selected ? 'primary' : 'default'}
							block
							icon={selected ? <LuArrowRight /> : undefined}
							iconPlacement="end"
							// Every card's button reads the same, so the name adds the
							// collection — after the visible text, so voice control matches.
							aria-label={
								selected
									? `Continue with ${collection.name}`
									: `Select ${collection.name}`
							}
							onClick={() => onSelect(collection)}>
							{selected ? 'Continue' : 'Select'}
						</Button>
					</div>
				)}
			</Flex>
		</Card>
	);
};

export default CollectionCard;
